// ** react imports
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';

// ** react router imports
import { useParams } from 'react-router-dom';

// ** redux imports
import {
  useUploadTaxFileDocument,
  useDeleteDocument,
  useGetTaxFileDocuments,
  useGetDocumentDownloadUrl,
} from '../hooks/apis/useDocuments';

// ** context
const DocumentContext = createContext(null);

export const DocumentProvider = ({ children }) => {
  // states
  const [error, setError] = useState(null);
  const fileInputTriggerRef = useRef(null);
  const [isUploaderVisible, setIsUploaderVisible] = useState(true);
  const [isHistoryMode, setHistoryMode] = useState(false);
  const MAX_FILES_ALLOWED = 10;

  // hooks
  const { taxFileId } = useParams();

  // api call's and mutations
  const {
    data: taxFileDocuments,
    getTaxFileDocuments,
    isLoading: isGetTaxFileDocumentsLoading,
    refetch: refetchGetTaxFileDocuments,
    isGetTaxFileDocumentsFetching,
  } = useGetTaxFileDocuments();

  const {
    uploadTaxFileDocument,
    isUploadTaxFileDocumentLoading,
    isUploadTaxFileDocumentFetching,
  } = useUploadTaxFileDocument();

  const { deleteDocument, isDeleteDocumentLoading, isDeleteDocumentFetching } =
    useDeleteDocument();

  const {
    getDocumentDownloadUrl,
    isGetDocumentDownloadUrlLoading,
    isGetDocumentDownloadUrlFetching,
  } = useGetDocumentDownloadUrl();

  // hooks
  useEffect(() => {
    if (taxFileId) {
      getTaxFileDocuments({
        taxFileId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFileId]);

  useEffect(() => {
    if (taxFileDocuments !== undefined) {
      if (taxFileDocuments?.totalDocuments !== 0) {
        setIsUploaderVisible(true);
      }
    }
  }, [taxFileDocuments]);

  const totalDocuments = taxFileDocuments?.totalDocuments;
  const hasReachedMaxFiles = totalDocuments >= MAX_FILES_ALLOWED;

  const registerFileInputTrigger = useCallback(trigger => {
    fileInputTriggerRef.current = trigger;
  }, []);

  const triggerAddDocument = useCallback(() => {
    if (fileInputTriggerRef.current) {
      fileInputTriggerRef.current();
      setIsUploaderVisible(currentStat => !currentStat);
    }
  }, []);

  const toggleUploaderVisibility = useCallback(() => {
    setIsUploaderVisible(prev => !prev);
  }, []);

  const toggleHistoryMode = useCallback(() => {
    setHistoryMode(prev => !prev);
  }, []);

  const uploadDocument = useCallback(
    async ({ file, files, questionId, onUploadError }) => {
      try {
        const formData = new FormData();

        // Handle both single file and multiple files
        if (files && Array.isArray(files)) {
          // Append multiple files to the FormData
          files.forEach((f, index) => {
            formData.append('documents', f);
          });
        } else if (file) {
          // Append single file to the FormData
          formData.append('documents', file);
        }

        if (questionId) {
          formData.append('questionId', questionId);
        }
        const response = await uploadTaxFileDocument({ taxFileId, formData });
        if (totalDocuments + (files ? files.length : 1) >= MAX_FILES_ALLOWED) {
          setIsUploaderVisible(false);
        } else {
          setIsUploaderVisible(true);
        }
        return response;
      } catch (err) {
        setError(err.message || 'Failed to upload document.');
        if (onUploadError) onUploadError(err);
        throw err;
      }
    },
    [uploadTaxFileDocument, taxFileId, totalDocuments, taxFileDocuments]
  );

  const removeDocument = useCallback(
    async documentId => {
      return await deleteDocument(documentId);
    },
    [deleteDocument]
  );

  const downloadDocument = useCallback(
    async file => {
      if (file.type === 'pre-populated' && file.documentId) {
        const result = await getDocumentDownloadUrl(file.documentId);
        if (result.data) {
          const downloadData = result.data.data;
          const link = document.createElement('a');
          link.href = downloadData.downloadURL;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          if (downloadData.downloadURL.startsWith('blob:')) {
            URL.revokeObjectURL(downloadData.downloadURL);
          }
        }
      }
    },
    [getDocumentDownloadUrl]
  );

  const showAddDocumentButton = !hasReachedMaxFiles && !isUploaderVisible;

  const value = React.useMemo(
    () => ({
      isHistoryMode,
      taxFileDocuments,
      isGetTaxFileDocumentsLoading,
      isGetTaxFileDocumentsFetching,
      isUploadTaxFileDocumentLoading,
      isUploadTaxFileDocumentFetching,
      isDeleteDocumentLoading,
      isDeleteDocumentFetching,
      isGetDocumentDownloadUrlLoading,
      isGetDocumentDownloadUrlFetching,
      error,
      setError,
      uploadDocument,
      removeDocument,
      downloadDocument,
      refetchGetTaxFileDocuments,
      showAddDocumentButton,
      triggerAddDocument,
      toggleUploaderVisibility,
      registerFileInputTrigger,
      isUploaderVisible,
      setIsUploaderVisible,
      MAX_FILES_ALLOWED,
      totalDocuments,
      hasReachedMaxFiles,
      toggleHistoryMode,
    }),
    [
      isHistoryMode,
      taxFileDocuments,
      isGetTaxFileDocumentsLoading,
      isGetTaxFileDocumentsFetching,
      isUploadTaxFileDocumentLoading,
      isUploadTaxFileDocumentFetching,
      isDeleteDocumentLoading,
      isDeleteDocumentFetching,
      isGetDocumentDownloadUrlLoading,
      isGetDocumentDownloadUrlFetching,
      error,
      uploadDocument,
      removeDocument,
      downloadDocument,
      refetchGetTaxFileDocuments,
      showAddDocumentButton,
      triggerAddDocument,
      toggleUploaderVisibility,
      registerFileInputTrigger,
      isUploaderVisible,
      MAX_FILES_ALLOWED,
      totalDocuments,
      hasReachedMaxFiles,
      toggleHistoryMode,
    ]
  );

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
};

DocumentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDocument = () => {
  const context = useContext(DocumentContext);
  if (context === undefined) {
    throw new Error('useDocument must be used within a DocumentProvider');
  }
  return context;
};
